/**
 * Welke klank bij welk moment hoort.
 *
 * De namen op de schijf zeggen niets (`FX_000` tot `FX_019`), dus de tabel hieronder is
 * geen gok maar een luisterronde: alle twintig zijn naar mp3 omgezet en afgeluisterd, en
 * de uitkomst staat in `~/cdrom-rip/kennisbank/06-klanken.md`.
 *
 * Het afspelen zelf blijft in `main.ts`; hier staat alleen wat waar bij hoort, zodat het
 * te toetsen is zonder het hele scherm op te bouwen.
 */
import { BASIC_CARDS, type Seat } from '@catan/contracts';
import type { GameView } from '@catan/rules';

/**
 * De effecten uit `FX.BCF`.
 *
 * Het afluisteren maakte meteen twee blunders zichtbaar die er lang in zaten. `FX_004`
 * speelde bij **elke bouwzet** en is in werkelijkheid het stuk van vijftien seconden dat
 * hoort bij het krijgen van de riddermacht. En bij de dobbelsteen klonk `FX_000`, wat het
 * klikje van het menu is.
 */
export const FX = {
  // `FX_000` staat hier bewust níét bij: dat is het menuklikje, en dat woont in de groep
  // `interface` — waar de klikjes horen. Het stond ooit onder de dobbelworp, wat precies
  // het soort verwisseling is waar deze tabel een eind aan maakt.
  mislukt: 'FX_001',
  kaartNeergelegd: 'FX_002',
  kaartenGeschud: 'FX_003',
  riddermachtGekregen: 'FX_004',
  nieuweRonde: 'FX_005',
  ruilMislukt: 'FX_006',
  grondstofErbij: 'FX_007',
  toernooi: 'FX_008',
  gelukt: 'FX_009',
  vlootGebouwd: 'FX_010',
  gebouwGebouwd: 'FX_011',
  riddermachtVerloren: 'FX_012',
  handelsmachtGekregen: 'FX_013',
  handelsmachtVerloren: 'FX_015',
  dobbelworp: 'FX_016',
  dobbelworpTwee: 'FX_017',
  nieuwSpel: 'FX_018',
} as const;

/**
 * De klikjes uit de tweede groep van `FX.BCF`, allemaal rond een tiende seconde.
 *
 * De speler heeft deze zeven nog niet benoemd — het invulvel staat er leeg bij. Deze
 * namen zeggen dus wáár ze klinken, niet wat ze zijn. Komt er ooit een luisterronde
 * overheen, dan verhuizen ze hier en nergens anders.
 */
export const UI = {
  klik: 'FX_000',
  handOpen: 'FX_001',
  venster: 'FX_003',
} as const;

/** Hoe lang `FX_005` het scherm bezet houdt, zodat de worp erachteraan komt. */
export const BEURTKLANK_MS = 1400;

/**
 * De twee worpklanken, om en om.
 *
 * `FX_016` en `FX_017` zijn allebei een dobbelworp — de speler noemde ze "dobbelworp" en
 * "dobbelworp 2". Steeds dezelfde nemen maakt van een worp een piepje dat je na tien
 * beurten niet meer hoort; afwisselen houdt het levend, en het is bovendien wat er op de
 * schijf staat: twee opnames van hetzelfde moment.
 */
export function worpKlank(worpnummer: number): string {
  return worpnummer % 2 === 0 ? FX.dobbelworp : FX.dobbelworpTwee;
}

/**
 * Wordt de gebeurtenisstapel in dit verslag opnieuw geschud?
 *
 * De jaarwisseling is het enige moment in het spel waarop er echt geschud wordt ("de
 * gebeurteniskaartenstapel is opnieuw geschud"), en `FX_003` is volgens de speler precies
 * dat geluid. Het stond tot nu toe onder een grondstof die wegvloog, waar het niets te
 * zoeken had.
 */
export function isGeschud(regel: string): boolean {
  return /opnieuw geschud/i.test(regel);
}

/**
 * Van kaartnaam naar het muziekstuk in `cards.BCF`.
 *
 * `cards.BCF` bevat achtenvijftig stukken, maar het is géén lijst van één stuk per kaart —
 * dat was jarenlang de aanname en die klopte niet. In twee volledig uitgespeelde potjes
 * onder Wine zijn er maar zestien ooit afgespeeld, sommige tot acht keer toe. De rest is
 * sfeermuziek, menumuziek en de stukken bij een verloren potje; die klinken nooit bij een
 * kaart.
 *
 * Hoe we deze tabel weten: Wine schrijft van elke klankbuffer de grootte mee, en elk
 * record heeft een eigen grootte — daarmee staat vast wélk stuk klonk. Een schermopname
 * erbij laat zien welke kaart op dat moment groot in beeld stond. Zie
 * `~/cdrom-rip/kennisbank/06-klanken.md`.
 *
 * Kaarten die hier niet in staan krijgen het korte effect. Dat is geen gebrek: lang niet
 * elke kaart heeft in het origineel een eigen stuk.
 */
export const KAART_MUZIEK: Readonly<Record<string, string>> = {
  Alchimist: 'cards_002',
  Bouwmeester: 'cards_005',
  Dorp: 'cards_009',
  'Zwarte ridder': 'cards_010',
  Karavaan: 'cards_012',
  Burgeroorlog: 'cards_013',
  'Goed jaar': 'cards_016',
  Vuurduivel: 'cards_017',
  Vooruitgang: 'cards_018',
  Kruidenheks: 'cards_022',
  Conflict: 'cards_029',
  // De kaart heet op de plaat 'Watervoorz.'; de speler noemde hem voluit.
  'Watervoorz.': 'cards_035',
  Rooftocht: 'cards_038',
  Verkenner: 'cards_041',
  Pest: 'cards_042',
  Spion: 'cards_043',
  Klooster: 'cards_044',
  Handelaar: 'cards_045',
};

/** De naam van het muziekstukje bij een kaart, of niets als die er niet is. */
export function kaartMuziek(cardId: number): string | undefined {
  const kaart = BASIC_CARDS.find((c) => c.id === cardId);
  return kaart === undefined ? undefined : KAART_MUZIEK[kaart.name];
}

/** Wat er van de weergave nodig is om te zien of er een beurt begint. */
type Beurtstand = Pick<GameView, 'phase' | 'turn'> & {
  readonly winner?: GameView['winner'];
};

export interface Beurtklank {
  /** Moet `FX_005` nu klinken? */
  readonly speel: boolean;
  /** De nieuwe stand om de volgende keer mee te vergelijken. */
  readonly stand: Seat | undefined;
}

/**
 * Klinkt het beurtklankje nu?
 *
 * Het origineel kondigt elke beurt aan: eerst dit korte stuk, dan de dobbelstenen
 * (`FX_016`). Bij ons stond `FX_005` wel in de tabel maar werd hij nergens afgespeeld —
 * de beurt begon in stilte.
 *
 * De weergave heeft geen "beurt begonnen"-bericht, en het scherm ververst veel vaker dan
 * er zetten zijn. Daarom kijkt dit naar wie er aan de worp toe is: verandert die stoel,
 * dan begint er een beurt. Zo klinkt hij precies één keer per beurt, ook als er tien keer
 * opnieuw getekend wordt, en net zo goed als de computer of de speler aan de overkant aan
 * de beurt is.
 */
export function volgendeBeurtklank(vorige: Seat | undefined, view: Beurtstand): Beurtklank {
  const stand = view.phase === 'dobbelen' && view.winner === undefined ? view.turn : undefined;
  return { speel: stand !== undefined && stand !== vorige, stand };
}

/** Eén grondstof die op een landschap komt te liggen. */
export interface Opbrengststap {
  readonly seat: Seat;
  readonly column: number;
  readonly side: GameView['you']['principality']['landscapes'][number]['side'];
  readonly landschap: GameView['you']['principality']['landscapes'][number]['landscape'];
  /** Hoeveel er op dit landschap bij kwamen. */
  readonly aantal: number;
}

/**
 * Wat er tussen twee standen aan grondstoffen bíj kwam.
 *
 * Het scherm liet de opbrengst niet zien: het getal op de tegel sprong omhoog terwijl de
 * dobbelsteen nog rolde. Om hem te kunnen laten aankomen moet je weten wélke landschappen
 * er iets bij kregen, en dat staat nergens in de weergave — de kern vertelt alleen de
 * nieuwe stand. Het verschil tussen vóór en ná de zet is het antwoord, en het klopt voor
 * álles wat grondstoffen oplevert: de worp, de keuze bij een goed jaar of een toernooi, en
 * de kaarten.
 *
 * Wat verdwijnt telt niet mee. Een roofoverval is geen opbrengst, en een landschap dat
 * leegloopt hoort geen grondstof te zien aankomen.
 */
export function opbrengstStappen(voor: GameView, nu: GameView): Opbrengststap[] {
  const stappen: Opbrengststap[] = [];
  for (const kant of ['you', 'opponent'] as const) {
    const eerder = voor[kant].principality.landscapes;
    for (const tegel of nu[kant].principality.landscapes) {
      const was = eerder.find((t) => t.column === tegel.column && t.side === tegel.side);
      const erbij = tegel.resources - (was?.resources ?? 0);
      if (erbij <= 0) continue;
      stappen.push({
        seat: nu[kant].seat,
        column: tegel.column,
        side: tegel.side,
        landschap: tegel.landscape,
        aantal: erbij,
      });
    }
  }
  return stappen;
}
